import { formatAssociationType } from './formatAssociationType';

export type DistributionItem = {
  label: string;
  count: number;
  percentage: number;
};

export type CategoryDistribution = {
  id: 'ancestry' | 'disease_model' | 'sex' | 'association_type' | 'chemical';
  title: string;
  items: DistributionItem[];
  totalAnnotated: number;
  totalUnspecified: number;
  totalRecords: number;
  isComplete: boolean;
  sampledAnnotated: number;
  sampleWindowSize?: number;
};

export type EvidenceDistributionMetrics = {
  totalRecords: number;
  ancestry: CategoryDistribution;
  diseaseModel: CategoryDistribution;
  sex: CategoryDistribution;
  associationType: CategoryDistribution;
  chemical: CategoryDistribution;
};

export function extractEvidenceLabels(raw: unknown): string[] {
  if (raw == null || raw === '' || raw === '-') return [];

  if (Array.isArray(raw)) {
    const labels: string[] = [];
    for (const item of raw) {
      if (typeof item === 'string' || typeof item === 'number') {
        const trimmed = String(item).trim();
        if (trimmed && trimmed !== '-') labels.push(trimmed);
      } else if (typeof item === 'object' && item !== null && 'name' in item) {
        const name = (item as { name?: unknown }).name;
        if (name != null && name !== '' && name !== '-') {
          labels.push(String(name).trim());
        }
      }
    }
    return labels;
  }

  if (typeof raw === 'object' && raw !== null && 'name' in raw) {
    const name = (raw as { name?: unknown }).name;
    return name != null && name !== '' && name !== '-' ? [String(name).trim()] : [];
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed || trimmed === '-') return [];
    if (trimmed.includes(',') && !trimmed.includes('(')) {
      return trimmed
        .split(',')
        .map(s => s.trim())
        .filter(s => s && s !== '-');
    }
    return [trimmed];
  }

  if (typeof raw === 'number') {
    return [String(raw)];
  }

  return [];
}

export function normalizeSexLabel(label: string): string {
  const lower = label.toLowerCase();
  if (lower === 'male') return 'Male';
  if (lower === 'female') return 'Female';
  if (lower === 'both') return 'Both';
  return label;
}

export function normalizeAssociationTypeLabel(label: string): string {
  const formatted = formatAssociationType(label);
  return formatted === '-' ? label : formatted;
}

function extractChemicalLabels(raw: unknown): string[] {
  if (raw == null || raw === '' || raw === '-') return [];

  if (Array.isArray(raw)) {
    const labels: string[] = [];
    for (const item of raw) {
      if (typeof item === 'string' || typeof item === 'number') {
        const trimmed = String(item).trim();
        if (trimmed && trimmed !== '-') labels.push(trimmed);
      } else if (typeof item === 'object' && item !== null) {
        const chem = item as { chemPrefName?: unknown; name?: unknown };
        const name = chem.chemPrefName ?? chem.name;
        if (name != null && name !== '' && name !== '-') {
          labels.push(String(name).trim());
        }
      }
    }
    return labels;
  }

  if (typeof raw === 'object' && raw !== null) {
    const chem = raw as { chemPrefName?: unknown; name?: unknown };
    const name = chem.chemPrefName ?? chem.name;
    return name != null && name !== '' && name !== '-' ? [String(name).trim()] : [];
  }

  return extractEvidenceLabels(raw);
}

function labelsFromRow(row: Record<string, unknown>, id: CategoryDistribution['id']): string[] {
  if (id === 'ancestry') {
    return extractEvidenceLabels(row.ancestry_name ?? row.ancestry ?? row.ancestryName);
  }
  if (id === 'disease_model') {
    return extractEvidenceLabels(
      row.disease_model_in_doc ?? row.disease_model ?? row.diseaseModel ?? row.diseaseModelInDoc,
    );
  }
  if (id === 'sex') {
    return extractEvidenceLabels(row.sex_in_doc ?? row.sex ?? row.gender ?? row.sexInDoc).map(
      normalizeSexLabel,
    );
  }
  if (id === 'chemical') {
    const fromSentence = extractChemicalLabels(row.chemsInSentence);
    if (fromSentence.length > 0) return fromSentence;

    const fromPrefName = extractChemicalLabels(row.chemPrefName ?? row.chemNames);
    if (fromPrefName.length > 0) return fromPrefName;

    return extractChemicalLabels(row.chemsIncludedInEvidenceBySource);
  }
  return extractEvidenceLabels(row.associationType ?? row.association_type ?? row.assocType ?? row.assoc_type)
    .map(normalizeAssociationTypeLabel)
    .filter(s => s && s !== '-');
}

export function buildCategoryFromAnnotatedSample(params: {
  id: CategoryDistribution['id'];
  title: string;
  rows: Array<Record<string, unknown>>;
  totalRecords: number;
  annotatedCount: number;
  sampleWindowSize?: number;
}): CategoryDistribution {
  const { id, title, rows, totalRecords } = params;
  const annotatedCount = Math.max(0, Math.min(params.annotatedCount, totalRecords));
  const totalUnspecified = Math.max(0, totalRecords - annotatedCount);
  const sampledAnnotated = rows.length;
  const isComplete = annotatedCount === 0 || sampledAnnotated >= annotatedCount;
  const sampleWindowSize = params.sampleWindowSize ?? rows.length;

  const counts = new Map<string, number>();
  for (const row of rows) {
    const labels = labelsFromRow(row, id);
    if (labels.length === 0) continue;
    for (const label of new Set(labels)) {
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
  }

  const percentageBase = isComplete ? Math.max(annotatedCount, 1) : Math.max(sampledAnnotated, 1);

  const items: DistributionItem[] = Array.from(counts.entries())
    .map(([label, count]) => ({
      label,
      count,
      percentage: Number(((count / percentageBase) * 100).toFixed(1)),
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  return {
    id,
    title,
    items,
    totalAnnotated: annotatedCount,
    totalUnspecified,
    totalRecords,
    isComplete,
    sampledAnnotated,
    sampleWindowSize,
  };
}

function calculateCategoryDistribution(
  id: CategoryDistribution['id'],
  title: string,
  totalRecords: number,
  valuesPerRecord: string[][],
): CategoryDistribution {
  const counts = new Map<string, number>();
  let totalAnnotated = 0;
  let totalUnspecified = 0;

  for (const recordValues of valuesPerRecord) {
    if (!recordValues || recordValues.length === 0) {
      totalUnspecified += 1;
      continue;
    }

    totalAnnotated += 1;
    const uniqueInRecord = new Set(recordValues);
    for (const label of uniqueInRecord) {
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
  }

  const items: DistributionItem[] = Array.from(counts.entries())
    .map(([label, count]) => ({
      label,
      count,
      percentage: totalAnnotated > 0 ? Number(((count / totalAnnotated) * 100).toFixed(1)) : 0,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  return {
    id,
    title,
    items,
    totalAnnotated,
    totalUnspecified,
    totalRecords,
    isComplete: true,
    sampledAnnotated: totalAnnotated,
  };
}

export function computeEvidenceDistributionMetrics(
  rows: Array<Record<string, unknown>> | null | undefined,
): EvidenceDistributionMetrics {
  const validRows = Array.isArray(rows) ? rows : [];
  const totalRecords = validRows.length;

  const ancestryPerRecord: string[][] = [];
  const modelPerRecord: string[][] = [];
  const sexPerRecord: string[][] = [];
  const assocTypePerRecord: string[][] = [];
  const chemicalPerRecord: string[][] = [];

  for (const row of validRows) {
    ancestryPerRecord.push(labelsFromRow(row, 'ancestry'));
    modelPerRecord.push(labelsFromRow(row, 'disease_model'));
    sexPerRecord.push(labelsFromRow(row, 'sex'));
    assocTypePerRecord.push(labelsFromRow(row, 'association_type'));
    chemicalPerRecord.push(labelsFromRow(row, 'chemical'));
  }

  return {
    totalRecords,
    ancestry: calculateCategoryDistribution('ancestry', 'Ancestry', totalRecords, ancestryPerRecord),
    diseaseModel: calculateCategoryDistribution(
      'disease_model',
      'Disease model',
      totalRecords,
      modelPerRecord,
    ),
    sex: calculateCategoryDistribution('sex', 'Sex / Gender', totalRecords, sexPerRecord),
    associationType: calculateCategoryDistribution(
      'association_type',
      'Association type',
      totalRecords,
      assocTypePerRecord,
    ),
    chemical: calculateCategoryDistribution('chemical', 'Chemical', totalRecords, chemicalPerRecord),
  };
}

export function metricsFromAnnotatedFieldSamples(params: {
  totalRecords: number;
  ancestry: { rows: Array<Record<string, unknown>>; annotatedCount: number };
  diseaseModel: { rows: Array<Record<string, unknown>>; annotatedCount: number };
  sex: { rows: Array<Record<string, unknown>>; annotatedCount: number };
  associationType: { rows: Array<Record<string, unknown>>; annotatedCount: number };
  chemical: { rows: Array<Record<string, unknown>>; annotatedCount: number };
}): EvidenceDistributionMetrics {
  const { totalRecords } = params;
  return {
    totalRecords,
    ancestry: buildCategoryFromAnnotatedSample({
      id: 'ancestry',
      title: 'Ancestry',
      rows: params.ancestry.rows,
      totalRecords,
      annotatedCount: params.ancestry.annotatedCount,
    }),
    diseaseModel: buildCategoryFromAnnotatedSample({
      id: 'disease_model',
      title: 'Disease model',
      rows: params.diseaseModel.rows,
      totalRecords,
      annotatedCount: params.diseaseModel.annotatedCount,
    }),
    sex: buildCategoryFromAnnotatedSample({
      id: 'sex',
      title: 'Sex / Gender',
      rows: params.sex.rows,
      totalRecords,
      annotatedCount: params.sex.annotatedCount,
    }),
    associationType: buildCategoryFromAnnotatedSample({
      id: 'association_type',
      title: 'Association type',
      rows: params.associationType.rows,
      totalRecords,
      annotatedCount: params.associationType.annotatedCount,
    }),
    chemical: buildCategoryFromAnnotatedSample({
      id: 'chemical',
      title: 'Chemical',
      rows: params.chemical.rows,
      totalRecords,
      annotatedCount: params.chemical.annotatedCount,
    }),
  };
}
